import { useDisclosure } from '@chakra-ui/hooks'
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import moment from 'moment'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiChevronLeft } from 'react-icons/bi'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { RiDeleteBin2Line } from 'react-icons/ri'
import { NavLink, useParams } from 'react-router-dom'
import type { CellProps, Column } from 'react-table'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { BaseLayout } from '..'
import AddNewAgencyChildUserModal from '../../components/Agency/AddNewAgencyChildUserModal'
import DeleteAgencyChildUserModal from '../../components/Agency/DeleteAgencyChildUserModal'
import Pagination from '../../components/Pagination'
import { UserDetaillsInfoItem } from '../../components/Profile/UserDetails'
import SearchBar from '../../components/SearchBar'
import type AgencyChildAccountResponse from '../../interfaces/AgencyChildAccountResponse'
import type { AgencyChildAccountResponseUser } from '../../interfaces/AgencyChildAccountResponse'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import useToast from '../../hooks/useToast'

const ManageAccountLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  const [userId, setUserId] = useState<string | null>(null)

  const { isOpen, onClose, onOpen } = useDisclosure()

  const {
    isOpen: isOpenDeleteModal,
    onClose: onCloseDeleteModal,
    onOpen: onOpenDeleteModal,
  } = useDisclosure({
    onClose: () => {
      setUserId(null)
    },
  })

  const { clientNumber } = useParams<{ clientNumber: string }>()

  const { user } = useContext(UserContext)

  const [data, setData] = useState<AgencyChildAccountResponse>()

  const { request, loading } = useFetch<AgencyChildAccountResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/agency/child/${clientNumber}`,
      )

      if (request.error) {
        toast({
          description: t('error_fetching_data', 'Error in fetching data. Try refreshing the page.'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  const tableData = useMemo<AgencyChildAccountResponseUser[]>(
    () => data?.users ?? [],
    [data],
  )

  const columns = useMemo<Array<Column<AgencyChildAccountResponseUser>>>(
    () => [
      {
        Header: t('user_name', 'User Name').toString(),
        accessor: ({ userName }) => userName,
      },
      {
        Header: t('user_email', 'User Email').toString(),
        accessor: ({ userEmail }) => userEmail,
      },
      {
        Header: t('role', 'Role').toString(),
        accessor: ({ roleDescription }) => roleDescription,
      },
      {
        Header: t('created_on', 'Created On').toString(),
        accessor: ({ createdAt }) => new Date(createdAt),
        sortType: 'datetime',
        Cell: ({ value }: CellProps<AgencyChildAccountResponseUser, Date>) =>
          moment(value).format('MMMM Do YYYY, h:mm:ss a'),
      },
      {
        Header: t('actions', 'Actions').toString(),
        accessor: ({ _id }) => _id,
        Cell: ({
          value,
        }: CellProps<AgencyChildAccountResponseUser, string>) => (
          <Button
            variant="link"
            colorScheme="appSlate"
            leftIcon={<Icon as={RiDeleteBin2Line} color="appBlue" />}
            onClick={() => {
              setUserId(value)
              onOpenDeleteModal()
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state: { pageIndex, pageSize, ...tableState },
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setGlobalFilter,
    setPageSize,
    previousPage,
    nextPage,
    gotoPage,
  } = useTable<AgencyChildAccountResponseUser>(
    { columns, data: tableData },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const pageOffset = pageIndex * pageSize

  return (
    <>
      <BaseLayout>
        <MainContentHeadingBox>
          <Heading
            as="h5"
            size="md"
            fontWeight="600"
            color={colors.appDarkIndigo}
            alignSelf="center"
          >
            {t('account_management', 'Account Management')}
          </Heading>
        </MainContentHeadingBox>
        <MainContentWrapper style={{ gap: '0.5em' }}>
          <Flex>
            <Button
              as={NavLink}
              variant="link"
              aria-label={t('navigate_back', 'Navigate Back')}
              leftIcon={<Icon as={BiChevronLeft} />}
              to="/manage-accounts"
            >
              Back to Accounts list
            </Button>
          </Flex>
          <Stack spacing={5} marginY={8}>
            <Heading
              as="h5"
              size="md"
              fontWeight="600"
              color={colors.appDarkIndigo}
            >
              {data?.clientName}
            </Heading>
            <Divider />
            <SimpleGrid columns={4} spacing={5}>
              <UserDetaillsInfoItem
                name={t('bill_to', 'Bill To')}
                value={data?.billTo}
              />
              <UserDetaillsInfoItem
                name={t('processing_region', 'Processing Region')}
                value={data?.region}
              />
            </SimpleGrid>
            <SimpleGrid columns={4} spacing={5}>
              <UserDetaillsInfoItem
                name={t('created_on', 'Created On')}
                value={moment(data?.createdAt).format('DD MMMM YYYY')}
              />
              <UserDetaillsInfoItem
                name={t('imagekit_id', 'ImageKit ID')}
                value={data?.imagekitId}
              />
            </SimpleGrid>
            <Divider />
          </Stack>
          <Flex justifyContent="space-between" marginY="3">
            <Button onClick={onOpen}>{t('add_user', 'Add User')}</Button>
            <HStack>
              {rows.length ? (
                <ChakraText fontWeight="500">
                  Showing {pageOffset + 1} to{' '}
                  {pageOffset + pageSize <= rows.length
                    ? pageOffset + pageSize
                    : rows.length}{' '}
                  of {rows.length} entries
                </ChakraText>
              ) : (
                <ChakraText fontWeight="500">No entries available</ChakraText>
              )}
              <SearchBar
                searchQuery={tableState.globalFilter as string}
                setSearchQuery={setGlobalFilter}
                minWidth="30em"
              />
            </HStack>
          </Flex>
          <Table {...getTableProps}>
            <Thead bgColor="appPaleGreyTwo">
              {headerGroups.map(headerGroup => {
                const { key, ...restHeaderGroupProps } =
                  headerGroup.getHeaderGroupProps()
                return (
                  <Tr key={key} {...restHeaderGroupProps}>
                    {headerGroup.headers.map(column => {
                      const { key: keyCol, ...restColumn } =
                        column.getHeaderProps(column.getSortByToggleProps())
                      return (
                        <Th key={keyCol} {...restColumn}>
                          {column.render('Header')}
                          {!column.disableSortBy ? (
                            <Box
                              as="span"
                              pl="2"
                              color={colors.appBattleshipGrey}
                            >
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <Icon
                                    as={FaSortUp}
                                    aria-label={t(
                                      'sorted_descending',
                                      'sorted descending',
                                    )}
                                  />
                                ) : (
                                  <Icon
                                    as={FaSortDown}
                                    aria-label={t(
                                      'sorted_ascending',
                                      'sorted ascending',
                                    )}
                                  />
                                )
                              ) : (
                                <Icon
                                  as={FaSort}
                                  aria-label={t(
                                    'sort_by_column',
                                    'sort by column',
                                  )}
                                />
                              )}
                            </Box>
                          ) : null}
                        </Th>
                      )
                    })}
                  </Tr>
                )
              })}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {!loading ? (
                rows.length ? (
                  page.map(row => {
                    prepareRow(row)
                    const { key, ...restRowProps } = row.getRowProps()
                    return (
                      <Tr key={key} {...restRowProps}>
                        {row.cells.map(cell => {
                          const { key: keyRow, ...restCellProps } =
                            cell.getCellProps()
                          return (
                            <Td key={keyRow} {...restCellProps}>
                              <ChakraText>{cell.render('Cell')}</ChakraText>
                            </Td>
                          )
                        })}
                      </Tr>
                    )
                  })
                ) : (
                  <Tr>
                    <Td colSpan={6} textAlign="center">
                      <ChakraText>No accounts available</ChakraText>
                    </Td>
                  </Tr>
                )
              ) : (
                <Tr>
                  <Td colSpan={6} textAlign="center">
                    <Spinner
                      thickness="4px"
                      speed="0.65s"
                      emptyColor="appPaleGrey"
                      color="appBlue"
                      size="xl"
                    />
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
          {pageOptions.length > 0 ? <Divider mt={4} mb={4} /> : null}
          <Flex justifyContent="flex-end" alignItems="center">
            <Pagination
              pageIndex={pageIndex}
              pageSize={pageSize}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageCount={pageCount}
              pageOptions={pageOptions}
              setPageSize={setPageSize}
              previousPage={previousPage}
              nextPage={nextPage}
              gotoPage={gotoPage}
            />
          </Flex>
        </MainContentWrapper>
      </BaseLayout>

      <AddNewAgencyChildUserModal
        childAccountClientNumber={clientNumber}
        getData={getData}
        isOpen={isOpen}
        onClose={onClose}
      />
      {userId ? (
        <DeleteAgencyChildUserModal
          isOpen={isOpenDeleteModal}
          onClose={onCloseDeleteModal}
          userId={userId}
          childAccountClientNumber={clientNumber}
          getData={getData}
        />
      ) : null}
    </>
  )
}

export default ManageAccountLayout
